/**
 * Haptics Utility - Provides haptic feedback for mobile devices
 * Issue #500 - Waitlist Polish Features
 */

import { getUserPreferences } from './preferences';

/**
 * Check if the Vibration API is supported
 */
export function isHapticsSupported(): boolean {
  return 'vibrate' in navigator;
}

/**
 * Trigger haptic feedback with a specific pattern
 * @param pattern - Single number (duration in ms) or array of durations [vibrate, pause, vibrate, ...]
 * @returns boolean - true if vibration was triggered, false if not supported or disabled
 */
export function vibrate(pattern: number | number[]): boolean {
  // Check if haptics is enabled in user preferences
  const preferences = getUserPreferences();
  if (!preferences.hapticsEnabled) {
    return false;
  }

  // Check if Vibration API is supported
  if (!isHapticsSupported()) {
    return false;
  }

  try {
    return navigator.vibrate(pattern);
  } catch (error) {
    console.warn('Haptic feedback failed:', error);
    return false;
  }
}

/**
 * Predefined haptic patterns for common interactions
 */
export const HapticPatterns = {
  /** Light tap - single short vibration (50ms) */
  LIGHT: 50,

  /** Medium tap - single medium vibration (100ms) */
  MEDIUM: 100,

  /** Heavy tap - single long vibration (200ms) */
  HEAVY: 200,

  /** Success - two short pulses [100ms, 50ms pause, 100ms] */
  SUCCESS: [100, 50, 100] as number[],

  /** Error - two short rapid pulses [50ms, 30ms pause, 50ms] */
  ERROR: [50, 30, 50] as number[],

  /** Notification - escalating pattern [50ms, 50ms pause, 100ms] */
  NOTIFICATION: [50, 50, 100] as number[],

  /** Warning - three short pulses [50ms, 30ms, 50ms, 30ms, 50ms] */
  WARNING: [50, 30, 50, 30, 50] as number[],

  /** Selection - very light tap (30ms) */
  SELECTION: 30,
};

/**
 * Convenience functions for common haptic patterns
 */

/** Trigger a light tap vibration */
export function vibrateLight(): boolean {
  return vibrate(HapticPatterns.LIGHT);
}

/** Trigger a medium tap vibration */
export function vibrateMedium(): boolean {
  return vibrate(HapticPatterns.MEDIUM);
}

/** Trigger a heavy tap vibration */
export function vibrateHeavy(): boolean {
  return vibrate(HapticPatterns.HEAVY);
}

/** Trigger a success vibration pattern */
export function vibrateSuccess(): boolean {
  return vibrate(HapticPatterns.SUCCESS);
}

/** Trigger an error vibration pattern */
export function vibrateError(): boolean {
  return vibrate(HapticPatterns.ERROR);
}

/** Trigger a notification vibration pattern */
export function vibrateNotification(): boolean {
  return vibrate(HapticPatterns.NOTIFICATION);
}

/** Trigger a warning vibration pattern */
export function vibrateWarning(): boolean {
  return vibrate(HapticPatterns.WARNING);
}

/** Trigger a selection vibration (very light) */
export function vibrateSelection(): boolean {
  return vibrate(HapticPatterns.SELECTION);
}
